import React from 'react';
import { useInput } from 'react-admin';
import { TextField, Typography, Box } from '@mui/material';

interface CodeEditorProps {
  source: string;
  label?: string;
  language?: string;
  readOnly?: boolean;
  height?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
}

/**
 * Simple code editor component for JSON/text editing
 * In a production environment, you might want to use a more sophisticated
 * code editor like Monaco Editor or CodeMirror
 */
const CodeEditor: React.FC<CodeEditorProps> = ({
  source,
  label,
  language = 'json',
  readOnly = false,
  height = '300px',
  defaultValue = '',
  onChange: onChangeCallback,
}) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useInput({ source, defaultValue });

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    onChange(newValue);
    onChangeCallback?.(newValue);
  };

  // Format JSON for display if it's valid JSON
  const formatValue = (val: any) => {
    if (!val) return defaultValue;
    
    if (language === 'json' && typeof val === 'object') {
      return JSON.stringify(val, null, 2);
    }
    
    if (language === 'json' && typeof val === 'string') {
      try {
        const parsed = JSON.parse(val);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return val;
      }
    }
    
    return val;
  };

  const displayValue = formatValue(value);

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}
      <TextField
        multiline
        fullWidth
        value={displayValue}
        onChange={handleChange}
        variant="outlined"
        disabled={readOnly}
        error={!!error}
        helperText={error?.message}
        InputProps={{
          style: {
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            fontSize: '14px',
            lineHeight: '1.4',
          },
        }}
        sx={{
          '& .MuiInputBase-root': {
            minHeight: height,
            alignItems: 'flex-start',
          },
          '& .MuiInputBase-input': {
            minHeight: `calc(${height} - 16px) !important`,
            resize: 'vertical',
          },
        }}
      />
      {language === 'json' && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Enter valid JSON. Objects will be automatically formatted.
        </Typography>
      )}
    </Box>
  );
};

export default CodeEditor;
